<template>
  <div class="p-4 overflow-y-auto h-full fade-in max-w-screen-xl w-full mx-auto">
    <!-- Breadcrumbs -->
    <Breadcrumbs :items="breadcrumbItems" text-color="text-gray-500" class="mb-6" />

    <!-- Profile Header -->
    <div class="max-w-4xl mx-auto mb-6">
      <div class="flex items-center gap-4">
        <div class="relative">
          <img
            v-if="userDetails?.avatar_image"
            :src="userDetails.avatar_image"
            alt="Avatar"
            class="w-20 h-20 rounded-full object-cover border"
          />
          <div
            v-else
            class="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center border"
          >
            <UserRound class="w-8 h-8 text-gray-400" />
          </div>
        </div>
        <div>
          <h1 class="text-2xl font-semibold">
            {{ userDetails?.first_name }} {{ userDetails?.last_name }}
          </h1>
          <p class="text-gray-600">@{{ userDetails?.username }}</p>
          <p class="text-sm text-gray-500">{{ userDetails?.email }}</p>
        </div>
      </div>
    </div>

    <!-- Settings Sections -->
    <div class="space-y-6 max-w-4xl mx-auto">
      <FellowExpeditionSetting :user-id="route.params.userId.toString()" />
      <FellowWeekPreference :user-id="Number(route.params.userId)" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed, watch } from 'vue'
import { UserRound } from 'lucide-vue-next'
import { useRoute } from 'vue-router'
import { useProfileStore } from '@/stores/profile'
import { useExpeditionsStore } from '@/stores/expeditions'
import FellowExpeditionSetting from '@/components/manage/FellowExpeditionSetting.vue'
import FellowWeekPreference from '@/components/manage/FellowWeekPreference.vue'

import Breadcrumbs from '@/components/ui/Breadcrumbs.vue'

const route = useRoute()
const profileStore = useProfileStore()
const expeditionsStore = useExpeditionsStore()

const userDetails = computed(() => {
  return (profileStore.profile as any)?.user_details
})

// Update page title when profile changes
watch(() => profileStore.profile, (newProfile) => {
  const profile = newProfile as any
  if (profile?.user_details?.first_name && profile?.user_details?.last_name) {
    document.title = `${profile.user_details.first_name} ${profile.user_details.last_name} Settings | Outtalent Campus`
  }
}, { immediate: true })

// Computed
const currentExpedition = computed(() => {
  return expeditionsStore.expeditions.find((expedition) =>
    expedition.adventurers.some((adv: any) => adv.id.toString() === route.params.userId)
  )
})

const currentUser = computed(() => {
  return currentExpedition.value?.adventurers.find(
    (adv: any) => adv.id.toString() === route.params.userId
  )
})

const breadcrumbItems = computed(() => [
  {
    label: 'Expedition Progress',
    to: `/manage/${currentExpedition.value?.id}`
  },
  {
    label: `${currentUser.value?.first_name || ''} ${currentUser.value?.last_name || ''}`.trim() || 'User',
    to: `/manage/${currentExpedition.value?.id}/${route.params.userId}`
  },
  {
    label: 'Fellow Settings'
  }
])



// Watch for user changes to fetch profile
watch(
  currentUser,
  async (newUser) => {
    if (newUser?.username) {
      await profileStore.fetchUserProfile(newUser.username)
    }
  },
  { immediate: true }
)

// Lifecycle
onMounted(async () => {
  await expeditionsStore.fetchExpeditions()
})
</script>

<style scoped></style>
